import { useCallback, useEffect, useRef } from 'react'
import { useNavigate, useRouter, useSearch } from '@tanstack/react-router'
import { parseSettingsSection, type SettingsSection } from '@/lib/settings-dialog'

/**
 * History-state marker on the entry `open()` pushed. Closing goes Back when
 * it is there (the user returns to exactly the entry they came from), and
 * replaces the URL otherwise (deep link, pasted URL) so history never grows.
 * Section switches carry it along (`state: true`).
 */
const PUSHED_KEY = 'hubSettingsDialog'

type AnySearch = Record<string, unknown>

/**
 * The only place that navigates for the settings dialog (board h39z):
 * open = push, switching sections = replace, close = Back when we pushed.
 * Usable from any component under the project route (sidebar, navbar, host).
 */
export function useSettingsDialog() {
  const navigate = useNavigate()
  const router = useRouter()
  const search = useSearch({ strict: false }) as AnySearch
  const section = parseSettingsSection(search.settings)
  const closingRef = useRef(false)

  useEffect(() => {
    if (!section) closingRef.current = false
  }, [section])

  const setSection = useCallback(
    (next: SettingsSection | undefined, mode: 'push' | 'replace') =>
      navigate({
        to: '.',
        search: ((prev: AnySearch) => ({ ...prev, settings: next })) as never,
        replace: mode === 'replace',
        state: (mode === 'push'
          ? (prev: AnySearch) => ({ ...prev, [PUSHED_KEY]: true })
          : true) as never,
      }),
    [navigate]
  )

  const open = useCallback(
    (next: SettingsSection) => {
      const current = parseSettingsSection(
        (router.state.location.search as AnySearch | undefined)?.settings
      )
      void setSection(next, current ? 'replace' : 'push')
    },
    [router, setSection]
  )

  const switchTo = useCallback(
    (next: SettingsSection) => void setSection(next, 'replace'),
    [setSection]
  )

  const close = useCallback(() => {
    if (closingRef.current) return
    closingRef.current = true
    const state = router.history.location.state as unknown as AnySearch | undefined
    if (state?.[PUSHED_KEY]) {
      router.history.back()
    } else {
      void setSection(undefined, 'replace')
    }
  }, [router, setSection])

  return { section, open, switchTo, close }
}
